import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getHoursCloseTicketsAuto } from '../../config';
import toastError from '../../errors/toastError';
import api from '../../services/api';

const useTickets = ({
  searchParam,
  pageNumber,
  status,
  startDate,
  endDate,
  showAll,
  userId,
  queueIds,
  withUnreadMessages,
  all,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  const [ticketsByUser, setTicketsByUser] = useState({});
  const [ticketsByConnection, setTicketsByConnection] = useState({});
  const [newContactsByDay, setNewContactsByDay] = useState({});
  const [contactsWithTicketsByDay, setContactsWithTicketsByDay] = useState([]);

  const formatDateToDDMMYYYY = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const closeTicket = useCallback(async (ticket) => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: 'closed',
        userId: ticket.userId || null,
      });
    } catch (err) {
      console.error('Erro ao fechar ticket automaticamente:', err.message);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets', {
        params: {
          searchParam,
          pageNumber,
          status,
          startDate,
          endDate,
          showAll,
          userId,
          queueIds,
          withUnreadMessages,
          all,
        },
      });

      setTickets(data.tickets);

      // Contatos únicos por dia
      const contactsByDay = data.tickets.reduce((acc, ticket) => {
        const contactName = ticket.contact?.name || 'Contato desconhecido';
        const createdAtDate = new Date(ticket.createdAt).toLocaleDateString();

        if (!acc[createdAtDate]) acc[createdAtDate] = new Set();
        acc[createdAtDate].add(contactName);

        return acc;
      }, {});

      const contactsWithTicketsByDayArr = Object.entries(contactsByDay).map(
        ([date, contacts]) => ({ date, count: contacts.size })
      );
      setContactsWithTicketsByDay(contactsWithTicketsByDayArr);

      // Contatos novos por dia
      const contactsPerDay = data.tickets.reduce((acc, ticket) => {
        const createdDate = new Date(ticket.createdAt).toLocaleDateString();
        acc[createdDate] = acc[createdDate] ? acc[createdDate] + 1 : 1;
        return acc;
      }, {});
      setNewContactsByDay(contactsPerDay);

      // Tickets por conexão
      const ticketsCountByConnection = data.tickets.reduce((acc, ticket) => {
        const connectionName = ticket.whatsapp?.name || 'Conexão desconhecida';
        const createdDate = formatDateToDDMMYYYY(ticket.createdAt);

        if (!acc[connectionName]) acc[connectionName] = {};
        if (!acc[connectionName][createdDate])
          acc[connectionName][createdDate] = 0;

        acc[connectionName][createdDate] += 1;
        return acc;
      }, {});
      setTicketsByConnection(ticketsCountByConnection);

      // Tickets por usuário
      const ticketsCountByUser = data.tickets.reduce((acc, ticket) => {
        const userID = ticket.userId;
        const createdDate = new Date(ticket.createdAt)
          .toISOString()
          .split('T')[0];

        if (!acc[userID]) acc[userID] = {};
        acc[userID][createdDate] = acc[userID][createdDate]
          ? acc[userID][createdDate] + 1
          : 1;

        return acc;
      }, {});
      setTicketsByUser(ticketsCountByUser);

      // Fechamento automático de tickets antigos
      const horasFecharAutomaticamente = getHoursCloseTicketsAuto();
      if (
        status === 'open' &&
        horasFecharAutomaticamente &&
        horasFecharAutomaticamente !== '' &&
        horasFecharAutomaticamente !== '0' &&
        Number(horasFecharAutomaticamente) > 0
      ) {
        const dataLimite = new Date();
        dataLimite.setHours(
          dataLimite.getHours() - Number(horasFecharAutomaticamente)
        );

        for (const ticket of data.tickets) {
          if (ticket.status !== 'closed') {
            const dataUltimaInteracaoChamado = new Date(ticket.updatedAt);
            if (dataUltimaInteracaoChamado < dataLimite) {
              await closeTicket(ticket);
            }
          }
        }
      }

      setHasMore(data.hasMore);
      setCount(data.count);
    } catch (err) {
      console.error('Erro na requisição:', err.message);
      toastError(err, t);
    } finally {
      setLoading(false);
    }
  }, [
    searchParam,
    pageNumber,
    status,
    startDate,
    endDate,
    showAll,
    userId,
    queueIds,
    withUnreadMessages,
    all,
    t,
    closeTicket,
  ]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTickets();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchTickets]);

  return {
    tickets,
    loading,
    hasMore,
    count,
    ticketsByUser,
    ticketsByConnection,
    formatDateToDDMMYYYY,
    newContactsByDay,
    contactsWithTicketsByDay,
    refetch: fetchTickets,
  };
};

export default useTickets;
